import React, { ReactElement, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Option } from 'react-select/src/filters';
import { useDispatch, useSelector } from 'react-redux';
import find from 'lodash/find';
import FormValidationError from '@shared/form/form-validation-error/form-validation-error';
import FieldLabel from '@shared/form/field-label/field-label';
import has from 'lodash/has';
import get from 'lodash/get';
import FormUtils from '@utils/form.utils';
import { useTranslation } from 'react-i18next';
import { SelectProps } from '../../../../types/form.types';
import FormSelectComponents from './form-select.components';
import {
  resetFieldValue,
  setFieldValue
} from '../../../../store/reducers/form-fields/form-fields.reducer';
import formFieldsSelectors from '../../../../store/selectors/form-fields.selectors';
import { useParamsContext } from '../../../../params/params-context';

const { StyledSelect, Container } = FormSelectComponents;

const FormSelect: React.FunctionComponent<SelectProps> = ({
  name,
  options,
  label,
  placeholder,
  errorMessage,
  isRequired,
  reverseColors = false,
  customWidth,
  showRequiredLegend
}) => {
  const dispatch = useDispatch();
  const { control, setValue, formState } = useFormContext();
  const storedValue = useSelector(formFieldsSelectors.getFieldValue)(name);
  const { t } = useTranslation();

  const selectOptions: Option[] = useMemo(() => {
    return options ? FormUtils.transformLabelsToOptions(options, t) : [];
  }, [options, t]);
  const { user } = useParamsContext();

  const value = useMemo(() => {
    const prefilledValue = has(user, name)
      ? { value: get(user, name), label: get(user, name) }
      : null;

    return find(selectOptions, { value: storedValue }) || prefilledValue;
  }, [name, selectOptions, storedValue, user]);

  useEffect(() => {
    setValue(name, value?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container customWidth={customWidth}>
      <FieldLabel
        text={label}
        isRequired={isRequired}
        showLegend={showRequiredLegend}
      />
      <Controller
        name={name}
        render={({ field }): ReactElement => (
          <StyledSelect
            classNamePrefix="Ucf-select"
            name={field.name}
            options={selectOptions}
            onChange={(option: Option): void => {
              field.onChange(option.value);
              dispatch(
                setFieldValue({
                  fieldName: name,
                  value: option.value,
                  faq_tag: option.data?.faq_tag ?? undefined
                })
              );
              if (name === 'case_reason') {
                dispatch(resetFieldValue('case_detailed_reason'));
                setValue('case_detailed_reason', undefined, {
                  shouldValidate: formState.isDirty
                });
              }
              if (name === 'main_category') {
                dispatch(resetFieldValue('sub_category'));
                setValue('sub_category', undefined, {
                  shouldValidate: formState.isDirty
                });
              }
            }}
            placeholder={t(placeholder)}
            data-test-id={name}
            value={value}
            reverseColors={reverseColors}
            isSearchable={false}
          />
        )}
        control={control}
      />
      <FormValidationError errorMessage={errorMessage} />
    </Container>
  );
};

export default FormSelect;
